import {
  Component,
  OnDestroy,
  ViewChild,
  inject,
} from '@angular/core';
import { forkJoin, Subject, Subscription } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  finalize,
  switchMap,
} from 'rxjs/operators';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { MessageService } from 'primeng/api';
import { Table, TableLazyLoadEvent } from 'primeng/table';
import { MyOskStudentsExamsService } from '../../../api/api-services/my-osk-students-exams.service';
import { OskStudentWordExamService } from '../../../api/api-services/osk-student-word-exam.service';
import {
  InternalExamResult,
  InternalExamSource,
} from '../../../api/internal-exam-classification.model';
import { IStudentExamVm } from '../../../api/student-exam-vm.model';
import { IWordExamVm } from '../../../api/word-exam-vm.model';
import { centerDynamicModalConfig } from '../../shared/helpers/modal.config';
import { ExamPdfDocumentService } from '../../shared/services/exam-pdf-document.service';
import { ExamViewComponent } from '../students/exam-view/exam-view.component';
import { ExamDateComponent } from '../students/exam-date/exam-date.component';
import { eModalRoles } from '../../shared/models/modals';

interface ExamSourceOption {
  label: string;
  value: InternalExamSource | null;
}

@Component({
  selector: 'app-exams',
  templateUrl: './exams.component.html',
  styleUrl: './exams.component.scss',
})
export class ExamsComponent implements OnDestroy {
  @ViewChild('examTable') private examTable?: Table;

  private readonly examsService = inject(MyOskStudentsExamsService);
  private readonly wordExamService = inject(OskStudentWordExamService);
  private readonly pdfDocumentService = inject(ExamPdfDocumentService);
  private readonly dialogService = inject(DialogService);
  private readonly messageService = inject(MessageService);
  private readonly searchSubject = new Subject<string>();
  private readonly subscriptions = new Subscription();
  private detailsDialog?: DynamicDialogRef;
  private dateDialog?: DynamicDialogRef;

  readonly InternalExamSource = InternalExamSource;

  exams: IStudentExamVm[] = [];
  selectedExams: IStudentExamVm[] = [];
  loading = false;
  printing = false;
  totalRecords = 0;
  first = 0;
  rows = 10;
  search = '';
  selectedSource: InternalExamSource | null = null;

  readonly sourceOptions: ExamSourceOption[] = [
    { label: 'Wszystkie egzaminy', value: null },
    { label: 'Egzaminy z OSK', value: InternalExamSource.Osk },
    {
      label: 'Egzaminy wykonane samodzielnie',
      value: InternalExamSource.Independent,
    },
  ];

  constructor() {
    this.subscriptions.add(
      this.searchSubject
        .pipe(debounceTime(350), distinctUntilChanged())
        .subscribe((search) => {
          this.search = search.trim();
          this.resetAndReload();
        })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.detailsDialog?.close();
    this.dateDialog?.close();
  }

  loadExams(event?: TableLazyLoadEvent): void {
    if (event) {
      this.first = event.first ?? this.first;
      this.rows = event.rows ?? this.rows;
    }

    const page = Math.floor(this.first / this.rows) + 1;

    this.loading = true;
    this.examsService
      .get(
        this.search || undefined,
        page,
        this.rows,
        this.selectedSource ?? undefined
      )
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (result) => {
          this.exams = result.data ?? [];
          this.totalRecords = result.allElementsCount ?? 0;
          this.selectedExams = this.selectedExams.filter((selected) =>
            this.exams.some((exam) => exam.guid === selected.guid)
          );
        },
        error: () => {
          this.exams = [];
          this.totalRecords = 0;
          this.showError('Nie udało się pobrać listy egzaminów.');
        },
      });
  }

  onSearch(value: string): void {
    this.searchSubject.next(value);
  }

  onSourceChange(): void {
    this.resetAndReload();
  }

  isPassed(result: InternalExamResult): boolean {
    return (
      result === InternalExamResult.PassedByOsk ||
      result === InternalExamResult.PassedAutomatically
    );
  }

  isFailed(result: InternalExamResult): boolean {
    return (
      result === InternalExamResult.FailedByOsk ||
      result === InternalExamResult.FailedAutomatically
    );
  }

  hasResult(exam: IStudentExamVm): boolean {
    return (
      exam.internalExamResult !== InternalExamResult.Undefined &&
      (this.isPassed(exam.internalExamResult) ||
        this.isFailed(exam.internalExamResult))
    );
  }

  sourceIcon(exam: IStudentExamVm): string {
    return exam.internalExamSource === InternalExamSource.Osk
      ? 'pi-car'
      : 'pi-home';
  }

  statusTooltip(exam: IStudentExamVm): string {
    const result = this.isPassed(exam.internalExamResult)
      ? 'Zaliczony'
      : 'Niezaliczony';
    const source =
      exam.internalExamSource === InternalExamSource.Osk
        ? 'egzamin uruchomiony przez OSK'
        : 'egzamin wykonany samodzielnie';
    return `${result} — ${source}`;
  }

  showExam(exam: IStudentExamVm): void {
    this.wordExamService.get(exam.guid, true).subscribe({
      next: (details) => {
        this.detailsDialog = this.dialogService.open(ExamViewComponent, {
          ...centerDynamicModalConfig,
          data: { wordExamDetails: details },
          width: '90vw',
          height: '90vh',
          baseZIndex: 10001,
        });
      },
      error: () => this.showError('Nie udało się pobrać szczegółów egzaminu.'),
    });
  }

  changeExamDate(exam: IStudentExamVm): void {
    this.dateDialog = this.dialogService.open(ExamDateComponent, {
      ...centerDynamicModalConfig,
      width: '579px',
      height: '695px',
      baseZIndex: 10001,
      data: {
        studentWordExamGuid: exam.guid,
        finishedDate: exam.completedAt ?? new Date(),
      },
    });

    this.dateDialog.onClose.subscribe(
      (res: { status: eModalRoles; examEndDate: Date }) => {
        if (res?.status === eModalRoles.REFRESH) {
          exam.completedAt = res.examEndDate;
        }
      }
    );
  }

  printExam(exam: IStudentExamVm): void {
    this.print([exam]);
  }

  printSelected(): void {
    this.print(this.selectedExams);
  }

  private print(exams: IStudentExamVm[]): void {
    if (this.printing || exams.length === 0) {
      return;
    }

    this.printing = true;
    forkJoin(exams.map((exam) => this.wordExamService.get(exam.guid, true)))
      .pipe(
        switchMap((details) => this.pdfDocumentService.generate(details)),
        finalize(() => (this.printing = false))
      )
      .subscribe({
        next: (blob) => this.downloadPdf(blob, exams),
        error: () => this.showError('Nie udało się wygenerować pliku PDF.'),
      });
  }

  private downloadPdf(blob: Blob, selected: IStudentExamVm[]): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download =
      selected.length === 1
        ? `Egzamin_wewnętrzny_${this.filePart(
            selected[0].displayName
          )}.pdf`
        : `Egzaminy_wewnętrzne_${new Date()
            .toISOString()
            .slice(0, 10)}.pdf`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  }

  private resetAndReload(): void {
    this.first = 0;
    this.selectedExams = [];
    if (this.examTable) {
      this.examTable.reset();
      return;
    }
    this.loadExams();
  }

  private filePart(value: string): string {
    return value.trim().replace(/[^\p{L}\p{N}-]+/gu, '_') || 'kursant';
  }

  private showError(detail: string): void {
    this.messageService.add({
      severity: 'error',
      summary: 'Błąd',
      detail,
    });
  }
}
